import type {
  RunPayloadScope,
  RunPayloadUrlResolver,
} from "../datasets/rift-dataset-payload.query";

/**
 * The authorization surface a snapshot dataset's run payloads must be fetched
 * through: the read-only cache scope plus the signed-URL resolver that hits
 * `publicBoards.getResultUrl` / `sharedBoards.getResultUrl`.
 */
export type SnapshotPayloadContext = {
  scope: RunPayloadScope;
  urlResolver: RunPayloadUrlResolver;
};

/**
 * Read-only payload context per snapshot dataset id.
 *
 * `mountBoardSnapshot` knows which resolver a read-only viewer may use, but the
 * consumers that ensure payloads on demand (rift queries, stat batches, board
 * filter option loading) only hold a dataset id and would otherwise fall back
 * to the org-scoped `runs.getResultUrl`, which an anonymous viewer cannot call.
 * Registering the context here lets `ensureDatasetPayload` resolve it centrally
 * instead of threading a scope through every call site.
 */
const snapshotPayloadContexts = new Map<string, SnapshotPayloadContext>();

/** Records the read-only payload context for a snapshot dataset. */
export const registerSnapshotPayloadContext = (
  datasetId: string,
  context: SnapshotPayloadContext,
): void => {
  snapshotPayloadContexts.set(datasetId, context);
};

/** Reads the registered read-only payload context for a dataset, if any. */
export const getSnapshotPayloadContext = (
  datasetId: string | null | undefined,
): SnapshotPayloadContext | undefined =>
  datasetId ? snapshotPayloadContexts.get(datasetId) : undefined;

/**
 * Drops a snapshot dataset's payload context. The expected `scope` is required
 * because two read-only boards can reference the same dataset: tearing down one
 * board must not strip the context the other is still resolving through.
 */
export const clearSnapshotPayloadContext = (
  datasetId: string,
  scope: RunPayloadScope,
): void => {
  if (snapshotPayloadContexts.get(datasetId)?.scope !== scope) return;
  snapshotPayloadContexts.delete(datasetId);
};

/** Test-only: clears the registry. */
export const __resetSnapshotPayloadContextsForTests = (): void => {
  snapshotPayloadContexts.clear();
};
